import React, { useRef, useCallback, useEffect, useState } from 'react';

const POINT_RADIUS = 5;
const POINT_HIT_RADIUS = 12;
const MAX_POINTS = 5;
const GRID_LINES = 4;
const ACCENT_COLOR = '#66ffb7';

/**
 * ToneCurve - Interactive canvas-based tone curve widget.
 *
 * Points are [[x, y], ...] in 0-255 range with linear interpolation.
 * Fixed endpoints at (0,0) and (255,255) are always present but not draggable.
 * Click on the curve to add a point. Double-click a point to remove it.
 *
 * @param {Array|null} points - Control points [[x,y],...] or null for identity
 * @param {function} onChange - Called with new points array (or null if all removed)
 * @param {boolean} disabled - Whether interaction is disabled
 */
export default function ToneCurve({ points = null, onChange, disabled = false }) {
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [dragging, setDragging] = useState(null);
  const [hovered, setHovered] = useState(null);

  // Full point list: fixed endpoints + user points, sorted by x
  const allPoints = getAllPoints(points);

  // Setup canvas DPI scaling and track size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      setCanvasSize({ w: rect.width, h: rect.height });
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  // Draw the curve
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { w, h } = canvasSize;
    if (w === 0 || h === 0) return;

    // Clear
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, w, h);

    // Grid lines
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    for (let i = 1; i < GRID_LINES; i++) {
      const pos = Math.round((i / GRID_LINES) * w) + 0.5;
      ctx.beginPath();
      ctx.moveTo(pos, 0);
      ctx.lineTo(pos, h);
      ctx.stroke();
      const posH = Math.round((i / GRID_LINES) * h) + 0.5;
      ctx.beginPath();
      ctx.moveTo(0, posH);
      ctx.lineTo(w, posH);
      ctx.stroke();
    }

    // Identity diagonal (reference line)
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h);
    ctx.lineTo(w, 0);
    ctx.stroke();

    // Curve - linear interpolation between sorted points
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < allPoints.length; i++) {
      const px = (allPoints[i][0] / 255) * w;
      const py = h - (allPoints[i][1] / 255) * h;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();

    // Draw user control points (not the fixed endpoints)
    const userPoints = points || [];
    for (let i = 0; i < userPoints.length; i++) {
      const px = (userPoints[i][0] / 255) * w;
      const py = h - (userPoints[i][1] / 255) * h;
      const isActive = hovered === i || dragging === i;

      ctx.beginPath();
      ctx.arc(px, py, POINT_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = isActive ? ACCENT_COLOR : '#ffffff';
      ctx.fill();
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }, [allPoints, points, hovered, dragging, canvasSize]);

  const getCanvasPos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return [0, 0];
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 255;
    const y = ((rect.height - (e.clientY - rect.top)) / rect.height) * 255;
    return [Math.round(Math.max(0, Math.min(255, x))), Math.round(Math.max(0, Math.min(255, y)))];
  }, []);

  const findPointIndex = useCallback((mx, my) => {
    const userPoints = points || [];
    const canvas = canvasRef.current;
    if (!canvas) return -1;
    const rect = canvas.getBoundingClientRect();

    for (let i = 0; i < userPoints.length; i++) {
      const px = (userPoints[i][0] / 255) * rect.width;
      const py = rect.height - (userPoints[i][1] / 255) * rect.height;
      const cx = mx - rect.left;
      const cy = my - rect.top;
      const dist = Math.sqrt((px - cx) ** 2 + (py - cy) ** 2);
      if (dist <= POINT_HIT_RADIUS) return i;
    }
    return -1;
  }, [points]);

  const handleMouseDown = useCallback((e) => {
    if (disabled) return;
    const idx = findPointIndex(e.clientX, e.clientY);
    if (idx >= 0) {
      setDragging(idx);
      e.preventDefault();
    }
  }, [disabled, findPointIndex]);

  const handleMouseMove = useCallback((e) => {
    if (disabled) return;

    if (dragging !== null) {
      const [x, y] = getCanvasPos(e);
      const userPoints = [...(points || [])];
      const prevX = dragging > 0 ? userPoints[dragging - 1][0] + 1 : 1;
      const nextX = dragging < userPoints.length - 1 ? userPoints[dragging + 1][0] - 1 : 254;
      const clampedX = Math.max(prevX, Math.min(nextX, x));
      userPoints[dragging] = [clampedX, y];
      onChange(userPoints);
    } else {
      const idx = findPointIndex(e.clientX, e.clientY);
      setHovered(idx >= 0 ? idx : null);
    }
  }, [disabled, dragging, points, onChange, getCanvasPos, findPointIndex]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  const handleClick = useCallback((e) => {
    if (disabled || dragging !== null) return;
    const idx = findPointIndex(e.clientX, e.clientY);
    if (idx >= 0) return;

    const userPoints = [...(points || [])];
    if (userPoints.length >= MAX_POINTS) return;

    const [x, y] = getCanvasPos(e);
    if (x <= 0 || x >= 255) return;

    userPoints.push([x, y]);
    userPoints.sort((a, b) => a[0] - b[0]);
    onChange(userPoints);
  }, [disabled, dragging, points, onChange, getCanvasPos, findPointIndex]);

  const handleDoubleClick = useCallback((e) => {
    if (disabled) return;
    const idx = findPointIndex(e.clientX, e.clientY);
    if (idx >= 0) {
      const userPoints = [...(points || [])];
      userPoints.splice(idx, 1);
      onChange(userPoints.length > 0 ? userPoints : null);
      setHovered(null);
    }
  }, [disabled, points, onChange, findPointIndex]);

  // Global mouseup to handle drag ending outside canvas
  useEffect(() => {
    if (dragging !== null) {
      const handleGlobalMouseUp = () => setDragging(null);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [dragging]);

  return (
    <div className="tone-curve-container">
      <canvas
        ref={canvasRef}
        className="tone-curve-canvas"
        style={{
          width: '100%',
          aspectRatio: '1',
          display: 'block',
          cursor: disabled ? 'default' : (dragging !== null ? 'grabbing' : 'crosshair'),
          borderRadius: 4,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onMouseLeave={() => { setHovered(null); }}
      />
    </div>
  );
}

/**
 * Build full sorted points array including fixed endpoints.
 */
function getAllPoints(userPoints) {
  const pts = [[0, 0], ...(userPoints || []), [255, 255]];
  pts.sort((a, b) => a[0] - b[0]);
  return pts;
}
